<template>
  <div class="page-node" :style="pageNodeStyle" @dblclick="onEdit">
    <!-- Handle entrant -->
    <Handle
      type="target"
      position="left"
      :id="`${id}-in`"
      class="handle handle-in"
    />

    <header class="page-header">
      <i class="fa-solid fa-file-circle-plus me-1"></i>
      <strong>{{ data?.label || 'Page' }}</strong>
      <span class="badge bg-info ms-2">{{ childrenCount }} item(s)</span>

      <div class="ms-auto btn-group btn-group-sm">
        <button class="btn btn-outline-primary" @click.stop="onEdit" title="Éditer la page">
          <i class="fas fa-edit"></i>
        </button>
        <button class="btn btn-outline-danger" @click.stop="onDelete" title="Supprimer la page">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </header>

    <div class="page-content">
      <slot></slot>

      <div v-if="childrenCount === 0" class="empty-message">
        <i class="fas fa-inbox fa-2x mb-2"></i>
        <p>Aucun item dans cette page</p>
        <small>Double-cliquez pour éditer</small>
      </div>
    </div>

    <!-- Handle sortant -->
    <Handle
      type="source"
      position="right"
      :id="`${id}-out`"
      class="handle handle-out"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Handle } from '@vue-flow/core'

const props = defineProps({
  id: String,
  data: Object,
  childrenCount: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['delete', 'edit'])

// Calculer la hauteur dynamique en fonction du nombre d'enfants
const pageNodeStyle = computed(() => {
  // Hauteur de base : header (50px) + padding (20px) + message vide (100px)
  const baseHeight = 170

  // Hauteur par enfant : chaque nested node fait ~120px + espacement
  const childHeight = 150

  // Hauteur minimale pour le message "Aucun item"
  const minHeight = 200

  // Calculer la hauteur totale
  const calculatedHeight = props.childrenCount > 0
    ? baseHeight + (props.childrenCount * childHeight)
    : minHeight

  return {
    height: `${calculatedHeight}px`
  }
})

function onDelete() {
  if (confirm('⚠️ Supprimer cette page et tous ses items ?')) {
    emit('delete', { nodeId: props.id })
  }
}

function onEdit() {
  emit('edit', { nodeId: props.id })
}
</script>

<style scoped>
.page-node {
  width: 600px;
  /* height dynamique via :style binding */
  border: 2px dashed #4dabf7;
  background: rgba(77, 171, 247, 0.08);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: visible; /* Changé de hidden à visible pour voir les nested nodes */
  cursor: pointer;
  transition: all 0.3s ease-in-out; /* Animation fluide lors du redimensionnement */
  position: relative; /* 🔹 important pour positionner les handles */
}

.page-node:hover {
  border-color: #339af0;
  background: rgba(77, 171, 247, 0.15);
  box-shadow: 0 4px 12px rgba(51, 154, 240, 0.2);
}

.page-header {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e7f5ff;
  border-bottom: 1px solid #a5d8ff;
  padding: 8px 12px;
  font-size: 0.9rem;
}

.badge {
  font-size: 0.75rem;
}

.page-content {
  flex: 1;
  position: relative;
  display: flex;
  align-items: flex-start; /* Changé de center à flex-start pour aligner les enfants en haut */
  justify-content: center;
  padding: 10px;
  min-height: 100px; /* Hauteur minimale pour le message vide */
}

.empty-message {
  text-align: center;
  color: #74c0fc;
  padding: 20px;
}

.empty-message p {
  margin: 8px 0;
  font-weight: 500;
}

.empty-message small {
  color: #a5d8ff;
}

/* --- Handles --- */
.handle {
  width: 20px;
  height: 20px;
  background: #339af0;
  border: 2px solid white;
  border-radius: 50%;
  position: absolute;
  z-index: 10;
  cursor: crosshair;
}

</style>
